import { useEffect, useState } from "hono/jsx";
import { marked } from "marked";
import { MusicXMLPreview } from "@/components/input/MusicXMLPreview.tsx";
import {
  estimateWidth,
  parseNoteToMusicXML,
} from "@/client/scripts/_music-parser.ts";
import type { QuestionFormat } from "@/lib/kvdex/models.ts";
import type { QuestionWithId } from "../repository.ts";

interface QuestionEditorProps {
  user: { id: string; name?: string };
  initialQuestion?: QuestionWithId;
}

export const QuestionEditor = (
  { user, initialQuestion }: QuestionEditorProps,
) => {
  const [error, setError] = useState<string | null>(null);
  const [text, setText] = useState(initialQuestion?.text || "");
  const [answer, setAnswer] = useState(initialQuestion?.answer || "");
  const [points, setPoints] = useState(initialQuestion?.points || 10);
  const [tags, setTags] = useState(initialQuestion?.tags.join(", ") || "");
  const [format, setFormat] = useState<QuestionFormat>("txt");
  const [showPreview, setShowPreview] = useState(false);

  // Initialize format with fallback logic
  useEffect(() => {
    if (initialQuestion) {
      let fmt = initialQuestion.format || "txt";
      if ((fmt as string) === "musicxml") fmt = "note";

      // Fallback: If text implies SVG but format is wrong/missing (e.g. lost during edit), force SVG
      if (initialQuestion.mediaUrl) {
        fmt = "svg";
      } else if (
        fmt === "txt" && initialQuestion.text &&
        initialQuestion.text.trim().startsWith("<svg")
      ) {
        fmt = "svg";
      }
      setFormat(fmt as QuestionFormat);
    }
  }, [initialQuestion]);

  const handleSubmit = async () => {
    setError(null);
    const url = initialQuestion
      ? `/api/quiz/questions/${initialQuestion.id}`
      : "/api/quiz/questions";
    const method = initialQuestion ? "PUT" : "POST";

    const body: Record<string, unknown> = {
      text,
      answer,
      points,
      tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      format,
    };

    if (initialQuestion && method === "PUT") {
      body.versionstamp = initialQuestion.versionstamp;
    }

    const res = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (res.ok) {
      globalThis.location.href = "/quiz/admin"; // Redirect back to list
    } else {
      const data = await res.json();
      setError(data.error || "Failed to save question");
    }
  };

  return (
    <div class="min-h-screen bg-base-200 p-6">
      <div class="max-w-4xl mx-auto">
        <div class="flex items-center justify-between mb-8">
          <div>
            <h1 class="text-3xl font-black">
              {initialQuestion ? "Edit Question" : "New Question"}
            </h1>
            <p class="opacity-60">
              {initialQuestion
                ? `ID: ${initialQuestion.id}`
                : "Create a new quiz question"}
            </p>
          </div>
          <a href="/quiz/admin" class="btn btn-ghost">Back to List</a>
        </div>

        {error && (
          <div class="alert alert-error mb-4">
            <span>{error}</span>
          </div>
        )}

        <div class="card bg-base-100 shadow-xl">
          <div class="card-body space-y-4">
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium">Question Text</label>
              <textarea
                class="textarea textarea-bordered w-full font-mono text-sm leading-relaxed"
                value={text}
                onInput={(e) =>
                  setText((e.target as HTMLTextAreaElement).value)}
                placeholder="Enter your question..."
                rows={6}
              />
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium">Answer</label>
                <input
                  type="text"
                  class="input input-bordered w-full"
                  value={answer}
                  onInput={(e) =>
                    setAnswer((e.target as HTMLInputElement).value)}
                  placeholder="Correct answer"
                />
              </div>
              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium">Points</label>
                <input
                  type="number"
                  class="input input-bordered w-full"
                  value={points}
                  onInput={(e) => setPoints(
                    parseInt((e.target as HTMLInputElement).value) || 10,
                  )}
                  min={1}
                />
              </div>
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium">Tags (comma-separated)</label>
              <input
                type="text"
                class="input input-bordered w-full"
                value={tags}
                onInput={(e) => setTags((e.target as HTMLInputElement).value)}
                placeholder="geography, capitals, easy"
              />
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium">Format</label>
              <select
                class="select select-bordered w-full"
                value={format}
                onChange={(e) =>
                  setFormat(
                    (e.target as HTMLSelectElement).value as QuestionFormat,
                  )}
              >
                <option value="txt">Plain Text</option>
                <option value="md">Markdown</option>
                <option value="svg">SVG Image</option>
                <option value="note">Music Notation</option>
              </select>
              {initialQuestion?.mediaUrl && (
                <div class="mt-1 text-xs">
                  <a
                    href={initialQuestion.mediaUrl}
                    target="_blank"
                    class="link link-primary"
                  >
                    View Cached SVG
                  </a>
                </div>
              )}
            </div>

            <div class="flex items-center gap-2">
              <input
                type="checkbox"
                class="toggle toggle-primary"
                checked={showPreview}
                onChange={() => setShowPreview(!showPreview)}
              />
              <span class="text-sm font-medium">Show Preview</span>
            </div>

            {showPreview && (
              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium">Content Preview</label>
                <div class="border rounded-lg p-4 bg-base-200 min-h-24">
                  {format === "txt" && (
                    <pre class="whitespace-pre-wrap font-mono text-sm">{text || "(empty)"}</pre>
                  )}
                  {format === "md" && (
                    <div
                      class="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{
                        __html: marked(text || "") as string,
                      }}
                    />
                  )}
                  {format === "svg" && (
                    <div class="w-full flex justify-center p-4 bg-white rounded max-h-64 overflow-auto">
                      {(initialQuestion
                          ? initialQuestion.ownerId === user.id
                          : true)
                        ? (
                          <div
                            class="w-full"
                            dangerouslySetInnerHTML={{ __html: text || "" }}
                          />
                        )
                        : (
                          <div class="text-sm opacity-60 italic">
                            Preview hidden for security
                          </div>
                        )}
                    </div>
                  )}
                  {format === "note" && (
                    <MusicXMLPreview
                      musicxml={parseNoteToMusicXML(text)}
                      width={estimateWidth(text)}
                    />
                  )}
                </div>
              </div>
            )}

            <div class="card-actions justify-end mt-4">
              <a href="/quiz/admin" class="btn btn-ghost">Cancel</a>
              <button
                type="button"
                class="btn btn-primary"
                onClick={handleSubmit}
              >
                {initialQuestion ? "Save Changes" : "Create Question"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
